import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import { Image as ImageIcon, UploadSimple, ArrowCounterClockwise, Globe, MagnifyingGlass, ListBullets, CheckCircle, XCircle, LinkedinLogo, ShareNetwork, Star } from '@phosphor-icons/react';
import { API, BACKEND_URL } from './adminConstants';
import { DEFAULT_LOGO_URL, resolveAssetUrl } from '../../hooks/useSiteLogo';

function IndexNowPanel() {
  const { getAuthHeader } = useAuth();
  const [discoveredUrls, setDiscoveredUrls] = useState([]);
  const [host, setHost] = useState('');
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    try {
      const [urls, l] = await Promise.all([
        axios.get(`${API}/admin/seo/urls`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/seo/indexnow/log`, { headers: getAuthHeader() }),
      ]);
      setDiscoveredUrls(urls.data?.urls || []);
      setHost(urls.data?.host || '');
      setLog(l.data?.submissions || []);
    } catch {
      toast.error('Failed to load IndexNow status');
    }
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/seo/indexnow/submit`,
        {},  // auto-discover server-side
        { headers: getAuthHeader() },
      );
      if (r.data.ok) {
        toast.success(`Submitted ${r.data.url_count} URL${r.data.url_count === 1 ? '' : 's'} to Bing & partners`);
      } else {
        toast.error(`IndexNow returned ${r.data.status_code}: ${r.data.response?.slice(0, 80)}`);
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (loading) return null;

  return (
    <div className="mt-10 max-w-2xl" data-testid="indexnow-panel">
      <h2 className="font-outfit font-bold text-xl text-slate-900 flex items-center gap-2 mb-1">
        <MagnifyingGlass size={22} weight="duotone" className="text-lime-600" />
        Search Engine Indexing
      </h2>
      <p className="text-sm text-slate-500 mb-4">
        Notify Bing, Yandex and other IndexNow-participating engines that your site content has changed. Bing automatically forwards submissions to Google, so this also accelerates Google indexing — useful right after publishing a new news story, case study or job.
      </p>

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Public URLs ready to submit</p>
            <p className="text-xs text-slate-500">{discoveredUrls.length} URLs discovered from CMS · scoped to <code className="font-mono">{host}</code></p>
          </div>
          <Button
            onClick={submit}
            disabled={submitting || discoveredUrls.length === 0}
            className="bg-lime-600 hover:bg-lime-700"
            data-testid="indexnow-submit-btn"
          >
            <MagnifyingGlass size={15} className="mr-1" />
            {submitting ? 'Notifying engines…' : 'Notify search engines now'}
          </Button>
        </div>
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer text-slate-700 font-medium select-none flex items-center gap-1"><ListBullets size={13} /> Preview the list</summary>
          <ul className="mt-2 space-y-0.5 max-h-56 overflow-y-auto font-mono text-[10.5px] text-slate-500 bg-slate-50 p-2 rounded">
            {discoveredUrls.map(u => <li key={u} className="truncate">{u}</li>)}
          </ul>
        </details>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 text-sm">Recent submissions</h3>
          <span className="text-xs text-slate-500">{log.length}</span>
        </div>
        {log.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500">No submissions yet — click <strong>Notify search engines now</strong> above.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="indexnow-log">
            {log.map(l => (
              <li key={l.id} className="px-5 py-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {l.ok ? <CheckCircle size={16} weight="fill" className="text-lime-600 shrink-0" /> : <XCircle size={16} weight="fill" className="text-red-500 shrink-0" />}
                  <span className="text-slate-700">{new Date(l.submitted_at).toLocaleString('en-GB')}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-700">{l.url_count} URLs</span>
                  <span className="text-slate-400">·</span>
                  <span className={`font-mono ${l.ok ? 'text-lime-700' : 'text-red-600'}`}>HTTP {l.status_code || 'err'}</span>
                </div>
                <span className="text-slate-400 truncate ml-2 max-w-[180px]">{l.submitted_by}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


export function SiteSettings() {
  const { getAuthHeader } = useAuth();
  const [logoUrl, setLogoUrl] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Header social button visibility + custom generic slot URL/label.
  // Each toggle defaults to ON so the header behaves exactly like
  // before until an admin toggles one off.
  const [socialToggles, setSocialToggles] = useState({ linkedin: true, generic: true, trustpilot: true });
  const [genericUrl, setGenericUrl] = useState('');
  const [genericLabel, setGenericLabel] = useState('');
  const [savingSocials, setSavingSocials] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/page-content/site_settings`);
      setLogoUrl(r.data?.header_logo_url || '');
      setSocialToggles({
        linkedin: r.data?.header_social_linkedin_enabled !== false,
        generic: r.data?.header_social_generic_enabled !== false,
        trustpilot: r.data?.header_social_trustpilot_enabled !== false,
      });
      setGenericUrl(r.data?.header_social_generic_url || '');
      setGenericLabel(r.data?.header_social_generic_label || '');
      // The PUT endpoint stamps updated_at/by on the wrapping document;
      // surface them via the raw page-content document (re-fetch the wrapper)
      try {
        const meta = await axios.get(`${API}/admin/site-settings-meta`, { headers: getAuthHeader() });
        setUpdatedAt(meta.data?.updated_at || null);
        setUpdatedBy(meta.data?.updated_by || null);
      } catch { /* meta endpoint optional */ }
    } catch {
      setLogoUrl('');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (newUrl) => {
    await axios.put(
      `${API}/admin/page-content/site_settings`,
      {
        header_logo_url: newUrl || '',
        header_social_linkedin_enabled: socialToggles.linkedin,
        header_social_generic_enabled: socialToggles.generic,
        header_social_trustpilot_enabled: socialToggles.trustpilot,
        header_social_generic_url: genericUrl,
        header_social_generic_label: genericLabel,
      },
      { headers: getAuthHeader() },
    );
    setLogoUrl(newUrl || '');
    window.dispatchEvent(new Event('site-logo-updated'));
  };

  const saveSocials = async (nextToggles, extras = {}) => {
    setSavingSocials(true);
    try {
      const nextGenericUrl = extras.generic_url !== undefined ? extras.generic_url : genericUrl;
      const nextGenericLabel = extras.generic_label !== undefined ? extras.generic_label : genericLabel;
      await axios.put(
        `${API}/admin/page-content/site_settings`,
        {
          header_logo_url: logoUrl || '',
          header_social_linkedin_enabled: nextToggles.linkedin,
          header_social_generic_enabled: nextToggles.generic,
          header_social_trustpilot_enabled: nextToggles.trustpilot,
          header_social_generic_url: nextGenericUrl,
          header_social_generic_label: nextGenericLabel,
        },
        { headers: getAuthHeader() },
      );
      setSocialToggles(nextToggles);
      if (extras.generic_url !== undefined) setGenericUrl(nextGenericUrl);
      if (extras.generic_label !== undefined) setGenericLabel(nextGenericLabel);
      // Fire the same event SiteHeader listens on so the top banner
      // updates without a page refresh.
      window.dispatchEvent(new Event('site-logo-updated'));
      toast.success('Header social settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSavingSocials(false);
  };

  const handleFile = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const up = await axios.post(`${API}/admin/upload-image`, form, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      await save(up.data.image_url);
      toast.success('Logo updated — visible site-wide now');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleReset = async () => {
    setUploading(true);
    try {
      await save('');
      toast.success('Logo reset to default');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setUploading(false);
  };

  if (loading) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div></div>;

  const isCustom = !!logoUrl;
  const currentPreview = isCustom ? resolveAssetUrl(logoUrl) : DEFAULT_LOGO_URL;

  return (
    <div data-testid="site-settings-panel">
      <div className="mb-6">
        <h1 className="font-outfit font-bold text-2xl text-slate-900 flex items-center gap-2">
          <Globe size={26} weight="duotone" className="text-lime-600" />
          Site Settings
        </h1>
        <p className="text-slate-500 text-sm">
          Global brand assets that affect every page of the website and intranet. Use this to swap the header logo seasonally (e.g. festive variant, awareness-day variant) — changes are live immediately, no redeploy required.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="font-semibold text-slate-900">Header Logo</h3>
            <p className="text-xs text-slate-500 mt-0.5">Shown in the top navigation bar across all public pages, the intranet, the admin sidebar, and the login / register screens.</p>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${isCustom ? 'bg-lime-100 text-lime-700' : 'bg-slate-100 text-slate-600'}`}>
            {isCustom ? 'Custom' : 'Default'}
          </span>
        </div>

        {/* Live preview against the same white header background */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 flex items-center justify-center min-h-[120px] mb-3">
          <img
            src={currentPreview}
            alt="Current site logo"
            className="h-16 w-auto object-contain"
            data-testid="site-logo-preview"
          />
        </div>
        {updatedAt && (
          <p className="text-[11px] text-slate-500 mb-3" data-testid="site-logo-meta">
            Last changed {new Date(updatedAt).toLocaleString('en-GB')}{updatedBy ? ` by ${updatedBy}` : ''}.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploading ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-lime-600 text-white hover:bg-lime-700'}`}>
            <UploadSimple size={15} />
            {uploading ? 'Uploading…' : (isCustom ? 'Replace logo' : 'Upload custom logo')}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
              data-testid="site-logo-file-input"
            />
          </label>

          {isCustom && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={uploading}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="site-logo-reset-btn"
                >
                  <ArrowCounterClockwise size={15} className="mr-1" />
                  Reset to default
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset header logo?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The site will revert to the original North Somerset Environment logo. You can upload a custom one again anytime.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset} className="bg-slate-700 hover:bg-slate-800">
                    Reset
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 space-y-1 max-w-2xl">
        <p className="flex items-center gap-1.5 font-semibold text-slate-800"><ImageIcon size={14} weight="duotone" /> Tips</p>
        <p>PNG or SVG with a transparent background looks best. Aim for a height of ~120 px (the header renders it at ~56 px) and a similar aspect ratio to the default logo so it sits comfortably in the navigation bar.</p>
        <p>Logos are stored in the database and included in nightly backups — your custom logo will survive redeploys.</p>
      </div>

      {/* Header Social Buttons — toggle on/off */}
      <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6 max-w-2xl" data-testid="header-socials-panel">
        <h3 className="font-semibold text-slate-900 mb-1">Header Social Buttons</h3>
        <p className="text-xs text-slate-500 mb-5">Turn each icon in the top banner on or off without a redeploy. Changes appear on the public site within a couple of seconds.</p>

        <div className="space-y-3">
          <SocialToggleRow
            icon={LinkedinLogo}
            iconClass="text-[#0A66C2]"
            label="LinkedIn"
            desc="Links to the North Somerset Environment Company LinkedIn page."
            enabled={socialToggles.linkedin}
            onChange={v => saveSocials({ ...socialToggles, linkedin: v })}
            disabled={savingSocials}
            testId="toggle-social-linkedin"
          />

          {/* Generic Socials row — toggle + editable label + URL */}
          <div className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50/50 transition-colors" data-testid="social-generic-row">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="mt-0.5 shrink-0"><ShareNetwork size={22} weight="fill" className="text-slate-500" /></div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">Generic Socials</p>
                  <p className="text-xs text-slate-500 mt-0.5">Repoint this slot at Facebook, TikTok, YouTube or any other channel without a code change.</p>
                </div>
              </div>
              <label className="shrink-0 inline-flex items-center gap-2 cursor-pointer select-none">
                <span className={`text-xs font-medium ${socialToggles.generic ? 'text-lime-700' : 'text-slate-400'}`}>{socialToggles.generic ? 'On' : 'Off'}</span>
                <input
                  type="checkbox"
                  checked={socialToggles.generic}
                  disabled={savingSocials}
                  onChange={e => saveSocials({ ...socialToggles, generic: e.target.checked })}
                  className="h-5 w-5 accent-lime-600 cursor-pointer"
                  data-testid="toggle-social-generic"
                />
              </label>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3 pl-9">
              <div>
                <label className="text-[11px] font-medium text-slate-600">Label (used for accessibility)</label>
                <input
                  type="text"
                  value={genericLabel}
                  onChange={e => setGenericLabel(e.target.value)}
                  onBlur={() => saveSocials(socialToggles, { generic_label: genericLabel })}
                  placeholder="Generic Socials"
                  className="w-full mt-1 px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-lime-500/40 focus:border-lime-400"
                  data-testid="generic-social-label-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">URL</label>
                <input
                  type="url"
                  value={genericUrl}
                  onChange={e => setGenericUrl(e.target.value)}
                  onBlur={() => saveSocials(socialToggles, { generic_url: genericUrl })}
                  placeholder="https://www.facebook.com/nsecltd"
                  className="w-full mt-1 px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-lime-500/40 focus:border-lime-400"
                  data-testid="generic-social-url-input"
                />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 pl-9">Changes save automatically when you click away from the field.</p>
          </div>

          <SocialToggleRow
            icon={Star}
            iconClass="text-amber-500"
            label="Trustpilot"
            desc="Links to the NSEC Trustpilot review page."
            enabled={socialToggles.trustpilot}
            onChange={v => saveSocials({ ...socialToggles, trustpilot: v })}
            disabled={savingSocials}
            testId="toggle-social-trustpilot"
          />
        </div>
      </div>

      <IndexNowPanel />
    </div>
  );
}

function SocialToggleRow({ icon: Icon, iconClass, label, desc, enabled, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
      <div className="flex items-start gap-3 min-w-0">
        <div className="mt-0.5 shrink-0"><Icon size={22} weight="fill" className={iconClass} /></div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
        </div>
      </div>
      <label className="shrink-0 inline-flex items-center gap-2 cursor-pointer select-none">
        <span className={`text-xs font-medium ${enabled ? 'text-lime-700' : 'text-slate-400'}`}>{enabled ? 'On' : 'Off'}</span>
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={e => onChange(e.target.checked)}
          className="h-5 w-5 accent-lime-600 cursor-pointer"
          data-testid={testId}
        />
      </label>
    </div>
  );
}
